// A small dependency-free rich-text editor (contentEditable + a formatting toolbar) for the
// agenda text fields. stores sanitized HTML. Semi-uncontrolled: the DOM is only re-seeded from
// `value` when the editor isn't focused (so external loads / date changes apply without eating
// keystrokes). reset between records by giving it a `key`.
import { useEffect, useRef, type ClipboardEvent, type ReactNode } from 'react';
import { normalizePastedHtml, sanitizeHtml } from '../lib/richtext.js';

const exec = (cmd: string) => {
  try { document.execCommand(cmd, false); } catch { /* execCommand unsupported - ignore */ }
};

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  minHeight = 72,
  disabled = false,
  className = '',
  ariaLabel,
}: {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  disabled?: boolean;
  className?: string;
  /** accessible name for the text area (data-ph is a CSS-only placeholder, so it names nothing). */
  ariaLabel?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (document.activeElement !== el && el.innerHTML !== (value || '')) el.innerHTML = value || '';
  }, [value]);

  const emit = () => {
    const el = ref.current;
    if (!el) return;
    let html = el.innerHTML;
    if (html === '<br>' || html === '<div><br></div>' || html === '<p><br></p>') html = '';
    onChange(sanitizeHtml(html));
  };

  // rich copy/paste: keep the pasted formatting (normalised + sanitized) instead of the browser's raw
  // messy markup, and fall back to plain text when there's no HTML on the clipboard.
  const onPaste = (e: ClipboardEvent<HTMLDivElement>) => {
    const html = e.clipboardData.getData('text/html');
    if (html && html.trim()) {
      e.preventDefault();
      const clean = normalizePastedHtml(html);
      try { document.execCommand('insertHTML', false, clean); } catch { /* ignore */ }
      emit();
    }
    // no HTML on the clipboard → let the default plain-text paste happen.
  };

  // the press itself is cancelled (pointer + the mouse events a touch emulates), so the text keeps its focus
  // and selection; the command runs on click, so a swipe that scrolls the toolbar on a phone applies nothing.
  const Btn = (cmd: string, label: ReactNode, title: string) => (
    <button
      type="button"
      className="rte-btn"
      title={title}
      aria-label={title}
      tabIndex={-1}
      onPointerDown={(e) => e.preventDefault()}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => { exec(cmd); emit(); }}
    >
      {label}
    </button>
  );

  if (disabled) {
    return (
      <div className={`rte rte-disabled ${className}`}>
        <div className="rte-area" style={{ minHeight }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(value || '') }} />
      </div>
    );
  }

  return (
    <div className={`rte ${className}`}>
      <div className="rte-tb">
        {Btn('bold', <b>B</b>, 'Bold')}
        {Btn('italic', <i>I</i>, 'Italic')}
        {Btn('underline', <u>U</u>, 'Underline')}
        {Btn('insertUnorderedList', '• List', 'Bulleted list')}
        {Btn('insertOrderedList', '1. List', 'Numbered list')}
        {Btn('removeFormat', 'Clear', 'Clear formatting')}
      </div>
      <div
        ref={ref}
        className="rte-area"
        contentEditable
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel ?? placeholder}
        data-ph={placeholder}
        style={{ minHeight }}
        onInput={emit}
        onBlur={emit}
        onPaste={onPaste}
        suppressContentEditableWarning
      />
    </div>
  );
}
